package ai.wagemix.web.screens.mixbuilder

import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Action
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Event
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Prompt
import ai.wagemix.web.screens.WMWebStrings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.web.screens.FSWebScreen
import com.varabyte.kobweb.compose.ui.Modifier

/**
 * The mix builder page body. [picker] renders the occupation picker sheet when the user asks to
 * add occupations; it receives the close callback. [topBarActions] lets the page wire Save/Share
 * (and Re-evaluate, for a mix generated from a job description) into the shell's top bar from the
 * same view model; the default renders [WMMixBuilderTopBarActions].
 */
@Composable
fun <
    P: Prompt,
    E: Event<P>,
    A: Action,
    PR: WMMixBuilderPresenter<P, E, A>,
    VM: WMBaseMixBuilderWebViewModel<P, E, A, PR>
> WMMixBuilderScreen(
    createViewModel: () -> VM,
    mixId: String?,
    strings: WMWebStrings,
    modifier: Modifier = Modifier,
    picker: @Composable (onClose: () -> Unit) -> Unit,
    topBarActions: @Composable (VM) -> Unit = { vm -> WMMixBuilderTopBarActions(vm, strings) }
) {
    FSWebScreen(createViewModel = createViewModel, key = mixId) { vm ->
        var pickerOpen by remember { mutableStateOf(false) }
        WMMixBuilderScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            draftState = vm.draftState,
            summaryState = vm.summaryState,
            chartModelState = vm.chartModelState,
            accessState = vm.accessState,
            chartPrefsState = vm.chartPrefsState,
            filterOptionsState = vm.filterOptionsState,
            reevaluatingState = vm.reevaluatingState,
            tutorialState = vm.tutorialState,
            chartLockedState = vm.chartLockedState,
            modifier = modifier,
            onAddOccupationClick = { pickerOpen = true },
            onRemoveOccupation = vm::userRemovedOccupation,
            onShareChange = vm::userChangedShare,
            onShareCommit = vm::userCommittedShare,
            onToggleLock = vm::userToggledLock,
            onToggleEnabled = vm::userToggledEnabled,
            onFiltersChange = vm::userChangedFilters,
            onChartPrefsChange = vm::userChangedChartPrefs,
            onPremiumClick = vm::userRequestedPremiumFeature,
            onDiscardClick = vm::userRequestedDiscard,
            onTutorialStepComplete = vm::userCompletedTutorialStep,
            onTutorialSkip = vm::userRequestedSkipTutorial
        )
        topBarActions(vm)
        if (pickerOpen) picker { pickerOpen = false }
        val prompt = vm.promptState.value
        WMMixBuilderPromptDialog(
            prompt = prompt,
            strings = strings,
            onResponse = { selected -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, selected)) },
            onConfirmSave = vm::userConfirmedSave
        )
    }
}

/** Re-evaluate, Save / Update and Share for the top bar; rendered into a portal slot the page provides. */
@Composable
fun <
    P: Prompt,
    E: Event<P>,
    A: Action,
    PR: WMMixBuilderPresenter<P, E, A>,
    VM: WMBaseMixBuilderWebViewModel<P, E, A, PR>
> WMMixBuilderTopBarActions(vm: VM, strings: WMWebStrings) {
    val draft = vm.draftState.value
    WMMixBuilderActions(
        strings = strings,
        savable = draft?.savable == true,
        sourceMixId = draft?.sourceMixId,
        dirty = draft?.dirty == true,
        fromJobDescription = draft?.fromJobDescription == true,
        reevaluating = vm.reevaluatingState.value,
        onSaveClick = vm::userRequestedSave,
        onUpdateClick = vm::userRequestedUpdate,
        onShareClick = vm::userRequestedShare,
        onReevaluateClick = vm::userRequestedReevaluate
    )
}
